import { FRAME_FORMATS, type FrameFormat } from '@praesto/shared'
import { useText } from '../i18n.js'
import { useKeepInView } from './keepInView.js'
import { usePopover } from './usePopover.js'

interface Props {
  /** Ein Format wurde gewählt — Frame mittig ins Blickfeld legen. */
  onFormat: (format: FrameFormat) => void
  /** Frei ziehen: Das Board wartet danach auf einen aufgezogenen Rahmen. */
  onDraw: () => void
  /** Frame um die aktuelle Auswahl legen. */
  onAround: () => void
  /** Ob gerade etwas ausgewählt ist — sonst ist „um die Auswahl" sinnlos. */
  hasSelection: boolean
  /** Nach oben aufklappen (untere Werkzeugleiste) oder nach unten (Seitenpanel). */
  direction?: 'up' | 'down'
  /** Auslöser ohne Beschriftung, für die enge Frame-Liste. */
  compact?: boolean
}

/**
 * Aufklappmenü zum Anlegen eines Frames.
 *
 * Früher legte ein Klick sofort einen Frame in der Größe des Browserfensters an.
 * Das war bequem und falsch: Ein Frame ist das, was später den Bildschirm füllt
 * oder als Beitrag herausgeht — sein Format gehört zum Zielmedium, nicht zur
 * zufälligen Fensterbreite dessen, der ihn anlegt. Zwei Personen am selben Board
 * bekamen so unterschiedlich geformte Frames.
 *
 * Aufbau wie beim Farbwähler: relativ positionierter Auslöser, absolut
 * positioniertes Menü. Geöffnet und geschlossen wird über den gemeinsamen
 * Riegel — es soll immer nur ein Menü offen stehen.
 */
export function FrameMenu({
  onFormat,
  onDraw,
  onAround,
  hasSelection,
  direction = 'up',
  compact = false,
}: Props): React.ReactElement {
  const t = useText()
  const { open, toggle, close, ref: rootRef } = usePopover<HTMLDivElement>()
  const popoverRef = useKeepInView<HTMLDivElement>()

  const waehle = (aktion: () => void): void => {
    close()
    aktion()
  }

  return (
    <div className="frame-menu" ref={rootRef}>
      <button
        type="button"
        className={compact ? 'button ghost icon' : 'insert-tool'}
        title={t('board.insert.frameTitle')}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={toggle}
      >
        {compact ? (
          '+'
        ) : (
          <>
            <span className="insert-icon">⬚</span>
            {t('board.insert.frame')}
          </>
        )}
      </button>

      {open && (
        <div className={`frame-popover is-${direction}`} role="menu" ref={popoverRef}>
          <span className="frame-menu-label">{t('frame.menu.formats')}</span>
          <div className="frame-format-row">
            {FRAME_FORMATS.map((format) => (
              <button
                key={format.key}
                type="button"
                role="menuitem"
                className="frame-format"
                title={t(format.labelKey)}
                onClick={() => waehle(() => onFormat(format))}
              >
                {/* Die Kachel zeigt das Verhältnis, nicht nur seinen Namen —
                    16:9 erkennt man schneller an der Form als am Wort. */}
                <span
                  className="frame-format-shape"
                  style={{ aspectRatio: `${format.ratio}` }}
                  aria-hidden="true"
                />
                <span className="frame-format-short">{format.short}</span>
                <span className="frame-format-name">{t(format.labelKey)}</span>
              </button>
            ))}
          </div>

          <div className="frame-menu-actions">
            <button
              type="button"
              role="menuitem"
              className="frame-menu-action"
              onClick={() => waehle(onDraw)}
            >
              <span className="frame-menu-action-name">{t('frame.menu.draw')}</span>
              <span className="frame-menu-action-hint">{t('frame.menu.drawHint')}</span>
            </button>
            <button
              type="button"
              role="menuitem"
              className="frame-menu-action"
              disabled={!hasSelection}
              onClick={() => waehle(onAround)}
            >
              <span className="frame-menu-action-name">{t('frame.menu.around')}</span>
              <span className="frame-menu-action-hint">
                {hasSelection ? t('frame.menu.aroundHint') : t('frame.menu.aroundEmpty')}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
